import React, { FC, useEffect, useState } from "react";
import { StepIndicator } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../models";
import { getExistingSubscriptionForChange, getIsPrepaid } from "../../../state/selectors/customerSelectors";
import {
    isServiceSubscription as isServiceSubscriptionSelector,
    isSubscriptionBroadband,
    isTeliaTryggSubscription as isTeliaTryggSubscriptionSelector,
} from "../../../../checkout/state/selectors/subscriptionsSelectors";
import { ConnectedAlert } from "../../../../components";
import { setSubscriptionStep } from "../../../state/actions/subscriptionStepsActions";
import { fetchPricePlansForEdit } from "../../../state/actions/customerActions";
import {
    getCompletedSubscriptionSteps,
    getCurrentSubscriptionStep,
} from "../../../state/selectors/subscriptionStepsSelectors";
import { getIsTraadlosTabActive } from "../../../state/selectors/fwaSelectors";
import { getAvailablePricePlans } from "../../../state/selectors/phoneNumbersSelectors";
import { getRetailerStepIndicatorData, isSubscriptionScope } from "../../../utils";
import ChooseSubscription from "../../organisms/ChooseSubscription/ChooseSubscription";
import { isBroadbandSubscription as isBroadbandOfferingCode } from "../../organisms/ChooseSubscription/utils";
import { PhoneNumber } from "../../organisms/PhoneNumber";
import SubscriptionUserInfo from "../../organisms/SubscriptionUserInfo";
import "./AddSubscriptionSteps.less";

const shouldSkipForOfferingCode = (code?: string): boolean => {
    if (!code) return false;
    return code.includes("TRYGG");
};

// The phone-number/SIM step is always the second step (index 1), regardless of its label text.
const PHONE_NUMBER_STEP_INDEX = 1;
// The product user step is always the last step (index 2), skipped for broadband unless it falls back to it.
const LAST_STEP_INDEX = 2;

const getDisplayedCompletedSteps = (
    completedStepsArray: number[],
    {
        isTraadlosTabActive,
        shouldSkipPhoneNumberStep,
        shouldHideLastStepForBroadband,
    }: {
        isTraadlosTabActive: boolean;
        shouldSkipPhoneNumberStep: boolean;
        shouldHideLastStepForBroadband: boolean;
    },
): number[] => {
    if (isTraadlosTabActive) {
        return [];
    }
    if (shouldSkipPhoneNumberStep) {
        return completedStepsArray
            .filter((s) => s !== PHONE_NUMBER_STEP_INDEX)
            .map((s) => (s > PHONE_NUMBER_STEP_INDEX ? s - 1 : s));
    }
    if (shouldHideLastStepForBroadband) {
        return completedStepsArray.filter((s) => s !== LAST_STEP_INDEX);
    }
    return completedStepsArray;
};

interface Props {
    id?: string;
}

const AddSubscriptionSteps: FC<Props> = ({ id }) => {
    const dispatch = useDispatch();
    const subscriptionId = id || "";
    const currentIndex = useSelector((state: RootState) => getCurrentSubscriptionStep(state, id));
    const completedSteps = useSelector((state: RootState) => getCompletedSubscriptionSteps(state, id));
    const isTeliaTrygg = useSelector((state: RootState) => isTeliaTryggSubscriptionSelector(state, id));
    const isBroadband = useSelector((state: RootState) => isSubscriptionBroadband(state, id));
    const isService = useSelector((state: RootState) => isServiceSubscriptionSelector(state, id));
    const isTraadlosTabActive = useSelector(getIsTraadlosTabActive);
    const existingSubscriptionForChange = useSelector(getExistingSubscriptionForChange);
    const existingChangeScope = existingSubscriptionForChange?.changeScope;
    const isSimScopeChange = existingChangeScope === "sim";
    const isProductUserScopeChange = existingChangeScope === "productUser";
    const isSubscriptionChangeScopeChange = existingChangeScope === "subscriptionChange";
    const hideStepIndicatorForScopedEdit =
        isSimScopeChange || isProductUserScopeChange || isSubscriptionChangeScopeChange;
    const isPrepaid = useSelector((state: RootState) => getIsPrepaid(state));
    const showOnlyPrepaidTab = isPrepaid && !existingSubscriptionForChange;
    const pricePlansKey = id || existingSubscriptionForChange?.phoneNumber;
    const availablePricePlans = useSelector((state: RootState) => getAvailablePricePlans(state, pricePlansKey));

    // Use sessionStorage to persist showOnlyMobileTab across page refresh
    const [showOnlyMobileTab, setShowOnlyMobileTab] = useState(() => {
        const stored = id ? sessionStorage.getItem(`showOnlyMobileTab_${id}`) : null;
        if (stored !== null) {
            return stored === "true";
        }
        return !!existingSubscriptionForChange || showOnlyPrepaidTab;
    });
    const [browsingOfferingCode, setBrowsingOfferingCode] = useState<string | undefined>();

    useEffect(() => {
        if (existingSubscriptionForChange || showOnlyPrepaidTab) {
            setShowOnlyMobileTab(true);
            if (!id) return;
            sessionStorage.setItem(`showOnlyMobileTab_${id}`, "true");
            // Also store phone number for re-triggering check after refresh
            if (existingSubscriptionForChange?.phoneNumber) {
                sessionStorage.setItem(`existingPhoneNumber_${id}`, existingSubscriptionForChange.phoneNumber);
            }
        }
    }, [existingSubscriptionForChange, showOnlyPrepaidTab, id]);

    // When editing an existing subscription from cart, fetch available price plans without
    // modifying rapidOrder or resetting any client-side fields.
    useEffect(() => {
        const phoneNumber = existingSubscriptionForChange?.phoneNumber;
        if (
            existingSubscriptionForChange &&
            phoneNumber &&
            availablePricePlans.length === 0 &&
            isSubscriptionScope(existingChangeScope)
        ) {
            dispatch(fetchPricePlansForEdit(phoneNumber, id));
        }
    }, [existingSubscriptionForChange, availablePricePlans.length, dispatch, id]);

    // Cleanup sessionStorage when subscription flow is complete (currentIndex > 2) or component unmounts
    useEffect(() => {
        if (currentIndex > 2) {
            sessionStorage.removeItem(`showOnlyMobileTab_${id}`);
            sessionStorage.removeItem(`existingPhoneNumber_${id}`);
        }
        return () => {
            // Don't remove on unmount if still in flow (currentIndex <= 2)
            // Only clean up when truly done
            if (currentIndex > 2) {
                sessionStorage.removeItem(`showOnlyMobileTab_${id}`);
                sessionStorage.removeItem(`existingPhoneNumber_${id}`);
            }
        };
    }, [currentIndex, id]);

    const shouldSkipPhoneNumberStep =
        currentIndex === 0 ? shouldSkipForOfferingCode(browsingOfferingCode) : isTeliaTrygg || isService;
    const { hide, labels } = getRetailerStepIndicatorData();
    const completedStepsArray = Array.from({ length: completedSteps }, (_, i) => i);

    const shouldHideLastStepForBroadband =
        currentIndex === 0
            ? isBroadbandOfferingCode(browsingOfferingCode)
            : isBroadband && currentIndex < LAST_STEP_INDEX;
    const effectiveLabels = isBroadband
        ? labels.map((l, i) => (i === PHONE_NUMBER_STEP_INDEX ? "SIM-kort" : l))
        : labels;
    const displayedLabels = effectiveLabels.filter((_, i) => {
        if (shouldSkipPhoneNumberStep && i === PHONE_NUMBER_STEP_INDEX) return false;
        if (shouldHideLastStepForBroadband && i === LAST_STEP_INDEX) return false;
        return true;
    });
    const fwaLabels = ["Abonnement"];

    return (
        <div className="add-subscription-steps">
            {!hide && !hideStepIndicatorForScopedEdit && (
                <StepIndicator
                    index={
                        isTraadlosTabActive ? 0 : shouldSkipPhoneNumberStep ? Math.min(currentIndex, 1) : currentIndex
                    }
                    onClick={(i) => {
                        if (isTraadlosTabActive) return;
                        if (shouldSkipPhoneNumberStep) {
                            // Map 2-step display indices to internal 3-step indices: 0→0, 1→2
                            const internalIndex = i === 0 ? 0 : 2;
                            if (internalIndex > completedSteps) return;
                            dispatch(setSubscriptionStep(subscriptionId, internalIndex));
                            return;
                        }
                        if (i > completedSteps) return;
                        dispatch(setSubscriptionStep(subscriptionId, i));
                    }}
                    numberOfSteps={isTraadlosTabActive ? 1 : displayedLabels.length}
                    labels={isTraadlosTabActive ? fwaLabels : displayedLabels}
                    completedSteps={getDisplayedCompletedSteps(completedStepsArray, {
                        isTraadlosTabActive,
                        shouldSkipPhoneNumberStep,
                        shouldHideLastStepForBroadband,
                    })}
                    kind="purple"
                />
            )}
            <ConnectedAlert />
            {(() => {
                switch (currentIndex) {
                    case 0:
                        return (
                            <ChooseSubscription
                                subscriptionId={subscriptionId}
                                onSelectedOfferingCodeChange={setBrowsingOfferingCode}
                                showOnlyMobileTab={showOnlyMobileTab}
                            />
                        );
                    case 1:
                        return (
                            <PhoneNumber
                                subscriptionId={subscriptionId}
                                existingSubscriptionEditScope={existingChangeScope}
                            />
                        );
                    case 2:
                        return <SubscriptionUserInfo />;
                    default:
                        return null;
                }
            })()}
        </div>
    );
};

export default AddSubscriptionSteps;
